import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX, Play, Pause, ChevronDown, SkipForward } from "lucide-react";

export default function AudioReader({ sections, text, label = "Listen to the lesson" }) {
  const [voices, setVoices]     = useState([]);
  const [voiceIdx, setVoiceIdx] = useState(0);
  const [playing, setPlaying]   = useState(false);
  const [showVoices, setShowVoices] = useState(false);
  const [rate, setRate]         = useState(0.9);
  const [currentSection, setCurrentSection] = useState(0);
  const uttRef = useRef(null);
  const sectionsRef = useRef([]);
  const playingRef = useRef(false);
  const synth = window.speechSynthesis;

  // Build sections array from either prop
  useEffect(() => {
    if (sections && sections.length > 0) {
      sectionsRef.current = sections.filter(s => s.text?.trim());
    } else if (text) {
      sectionsRef.current = [{ title: "", text }];
    }
  }, [sections, text]);

  useEffect(() => {
    const load = () => {
      const v = synth.getVoices().filter(v => v.lang.startsWith("en"));
      setVoices(v);
    };
    load();
    synth.addEventListener("voiceschanged", load);
    return () => {
      synth.removeEventListener("voiceschanged", load);
      synth.cancel();
    };
  }, []);

  useEffect(() => { return () => { synth.cancel(); setPlaying(false); playingRef.current = false; }; }, [sections, text]);

  const speakSection = (index) => {
    if (index >= sectionsRef.current.length) {
      setPlaying(false);
      playingRef.current = false;
      setCurrentSection(0);
      return;
    }

    const section = sectionsRef.current[index];
    setCurrentSection(index);

    // Speak the title first with a pause, then the content
    const speakPart = (partText, onDone) => {
      const utt = new SpeechSynthesisUtterance(partText);
      utt.voice = voices[voiceIdx] || null;
      utt.rate = rate;
      utt.pitch = 1.05;
      utt.onend = onDone;
      utt.onerror = () => { setPlaying(false); playingRef.current = false; };
      uttRef.current = utt;
      synth.speak(utt);
    };

    if (section.title) {
      // Read title, pause, then read content
      speakPart(section.title, () => {
        if (!playingRef.current) return;
        // Pause between title and content
        setTimeout(() => {
          if (!playingRef.current) return;
          speakPart(section.text, () => {
            if (!playingRef.current) return;
            // Pause between sections
            setTimeout(() => {
              if (!playingRef.current) return;
              speakSection(index + 1);
            }, 800);
          });
        }, 500);
      });
    } else {
      speakPart(section.text, () => {
        if (!playingRef.current) return;
        setTimeout(() => {
          if (!playingRef.current) return;
          speakSection(index + 1);
        }, 800);
      });
    }
  };

  const startPlaying = () => {
    synth.cancel();
    setPlaying(true);
    playingRef.current = true;
    speakSection(0);
  };

  const stopPlaying = () => {
    synth.cancel();
    setPlaying(false);
    playingRef.current = false;
  };

  const toggle = () => {
    if (playing) stopPlaying();
    else startPlaying();
  };

  const skipToNext = () => {
    synth.cancel();
    if (currentSection + 1 < sectionsRef.current.length) {
      speakSection(currentSection + 1);
    } else {
      stopPlaying();
    }
  };

  if (!("speechSynthesis" in window)) return null;

  const totalSections = sectionsRef.current.length;
  const currentTitle = sectionsRef.current[currentSection]?.title || "";

  return (
    <div className="rounded-2xl p-3 border-2" style={{ background: "#E0F7FF", borderColor: "#4CC9F0" }}
      data-testid="audio-reader">
      <div className="flex items-center gap-3">
        {/* Play/pause */}
        <button onClick={toggle}
          className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-md transition-all hover:scale-110 active:scale-95 flex-shrink-0"
          style={{ background: playing ? "#EF476F" : "#118AB2" }}
          data-testid="audio-play-btn">
          {playing ? <Pause size={20} strokeWidth={2.5} /> : <Play size={20} strokeWidth={2.5} />}
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-xs font-black text-[#118AB2] uppercase tracking-wide mb-0.5">
            {playing ? (currentTitle || "Reading...") : label}
          </p>
          {playing ? (
            <div className="flex items-center gap-2">
              <div className="flex items-end gap-0.5 h-4">
                {[1,2,3,4,5,6,7,8].map(i => (
                  <div key={i} className="w-1 rounded-full"
                    style={{ background: "#118AB2", height: `${Math.random()*12+4}px`,
                      animation: `mascotBob ${0.4+i*0.05}s ease-in-out infinite alternate` }} />
                ))}
              </div>
              {totalSections > 1 && (
                <span className="text-[10px] font-bold text-[#118AB2]/60">
                  {currentSection + 1}/{totalSections}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 font-medium truncate">
              {totalSections > 1 ? `${totalSections} sections` : sectionsRef.current[0]?.text?.substring(0,50) + "..."}
            </p>
          )}
        </div>

        {/* Skip button */}
        {playing && totalSections > 1 && (
          <button onClick={skipToNext}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-white border border-[#4CC9F0] transition-all hover:scale-110 active:scale-95"
            data-testid="audio-skip-btn">
            <SkipForward size={14} color="#118AB2" strokeWidth={2.5} />
          </button>
        )}

        {/* Voice picker */}
        {voices.length > 1 && (
          <div className="relative">
            <button onClick={() => setShowVoices(!showVoices)}
              className="flex items-center gap-1 text-xs font-bold text-[#118AB2] bg-white rounded-xl px-2 py-1.5 border border-[#4CC9F0]"
              data-testid="voice-picker-btn">
              <Volume2 size={14} />
              <ChevronDown size={12} />
            </button>
            {showVoices && (
              <div className="absolute right-0 bottom-10 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 w-52 max-h-48 overflow-y-auto">
                <p className="text-xs font-black text-gray-500 px-3 pt-2 pb-1 border-b">Choose Voice</p>
                {voices.map((v, i) => (
                  <button key={i} onClick={() => { setVoiceIdx(i); setShowVoices(false); }}
                    className={`w-full text-left px-3 py-2 text-xs font-medium hover:bg-blue-50 transition-colors ${i===voiceIdx?"bg-[#E0F4FF] text-[#118AB2] font-black":""}`}
                    data-testid={`voice-option-${i}`}>
                    {v.name.replace(/\(.*\)/, "").trim()}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Speed */}
        <select value={rate} onChange={e => setRate(Number(e.target.value))}
          className="text-xs font-bold text-[#118AB2] bg-white rounded-xl px-1.5 py-1.5 border border-[#4CC9F0] cursor-pointer"
          data-testid="speed-select">
          <option value={0.7}>Slow</option>
          <option value={0.9}>Normal</option>
          <option value={1.2}>Fast</option>
        </select>
      </div>
    </div>
  );
}
